/**
 * Collection Filters Component
 *
 * Advanced filtering using Shopify's Section Rendering API (no page reload).
 * Similar to Dawn theme's approach.
 *
 * Usage in Liquid:
 * <div x-data="collectionFilters"
 *      data-section-id="{{ section.id }}"
 *      data-active-filters-count="{{ active_filters_count | default: 0 }}">
 */

export default function collectionFilters() {
  return {
    mobileFiltersOpen: false,   // overlay drawer (< lg)
    filtersVisible: true,       // inline sidebar shown/hidden (lg+). Persists across no-reload re-renders since it lives on the container component.
    activeFiltersCount: 0,
    isLoading: false,
    sectionId: null,
    booleanFilters: {},  // Track optimistic state for boolean filters

    init() {
      const el = this.$el
      this.sectionId = el.dataset.sectionId
      this.activeFiltersCount = parseInt(el.dataset.activeFiltersCount) || 0

      // Initialize boolean filters state from URL
      this.initBooleanFiltersState()

      // Calculate active filters count from URL
      this.updateActiveFiltersCount()

      // Close mobile drawer on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.mobileFiltersOpen) {
          this.mobileFiltersOpen = false
        }
      })

      // Handle browser back/forward navigation
      window.addEventListener('popstate', () => {
        this.fetchAndRenderSection(window.location.href, false)
      })
    },

    initBooleanFiltersState() {
      const url = new URL(window.location.href)
      // Reset and populate from current URL
      this.booleanFilters = {}
      for (const [key, value] of url.searchParams) {
        if (key.startsWith('filter.') && value === '1') {
          this.booleanFilters[key] = true
        }
      }
    },

    isBooleanFilterActive(paramName) {
      // Check optimistic state if available, otherwise fall back to URL
      if (paramName in this.booleanFilters) {
        return this.booleanFilters[paramName]
      }
      const url = new URL(window.location.href)
      return url.searchParams.has(paramName)
    },

    updateActiveFiltersCount() {
      const url = new URL(window.location.href)
      let count = 0

      for (const [key] of url.searchParams) {
        if (key.startsWith('filter.')) {
          count++
        }
      }

      this.activeFiltersCount = count
    },

    async fetchAndRenderSection(url, updateHistory = true) {
      this.isLoading = true

      try {
        // Build URL with section_id parameter for Section Rendering API
        const fetchUrl = new URL(url)
        fetchUrl.searchParams.set('section_id', this.sectionId)

        const response = await fetch(fetchUrl.toString(), {
          headers: {
            'Accept': 'text/html',
          }
        })

        if (!response.ok) throw new Error('Network response was not ok')

        const html = await response.text()

        // Replace the entire section content
        const container = document.getElementById('collection-filters-container')
        if (container) {
          // Parse the new HTML
          const parser = new DOMParser()
          const doc = parser.parseFromString(html, 'text/html')

          // Find the new container in the response
          const newContainer = doc.getElementById('collection-filters-container')
          if (newContainer) {
            // Replace inner content while preserving the container
            container.innerHTML = newContainer.innerHTML

            // Re-read the active filters count from new data
            const newCount = newContainer.dataset.activeFiltersCount
            this.activeFiltersCount = parseInt(newCount) || 0
          }
        }

        // Update URL without page reload
        if (updateHistory) {
          history.pushState({}, '', url)
        }

        // Update active filters count from URL
        this.updateActiveFiltersCount()

        // Reset optimistic boolean filter state after render
        this.booleanFilters = {}

        // Scroll to top of collection section smoothly
        this.$nextTick(() => {
          const collectionContainer = document.getElementById('collection-filters-container')
          if (collectionContainer) {
            collectionContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })

      } catch (error) {
        console.error('Section rendering error:', error)
        // Fallback to full page reload on error
        window.location.href = url
      } finally {
        this.isLoading = false
      }
    },

    applyFilter(url) {
      this.fetchAndRenderSection(url.toString())
    },

    toggleFilter(paramName, value) {
      const url = new URL(window.location.href)
      const currentValues = url.searchParams.getAll(paramName)

      if (currentValues.includes(value)) {
        // Remove this specific value
        url.searchParams.delete(paramName)
        currentValues.filter(v => v !== value).forEach(v => {
          url.searchParams.append(paramName, v)
        })
      } else {
        // Add the value
        url.searchParams.append(paramName, value)
      }

      // Reset to page 1 when filtering
      url.searchParams.delete('page')

      this.applyFilter(url)
    },

    removeFilter(paramName, value) {
      const url = new URL(window.location.href)
      const currentValues = url.searchParams.getAll(paramName)

      url.searchParams.delete(paramName)
      currentValues.filter(v => v !== value).forEach(v => {
        url.searchParams.append(paramName, v)
      })

      // Reset to page 1
      url.searchParams.delete('page')

      this.applyFilter(url)
    },

    updatePriceFilter(paramName, type, value) {
      const url = new URL(window.location.href)

      // Construct filter parameter name (e.g., filter.v.price.gte or filter.v.price.lte)
      const filterParam = type === 'min' ? `${paramName}.gte` : `${paramName}.lte`

      if (value && parseFloat(value) > 0) {
        // Convert to cents for Shopify
        const cents = Math.round(parseFloat(value) * 100)
        url.searchParams.set(filterParam, cents)
      } else {
        url.searchParams.delete(filterParam)
      }

      // Reset to page 1
      url.searchParams.delete('page')

      this.applyFilter(url)
    },

    toggleBooleanFilter(paramName, value) {
      // Prevent multiple clicks while loading
      if (this.isLoading) return

      const url = new URL(window.location.href)
      const currentValue = url.searchParams.get(paramName)
      const isCurrentlyActive = currentValue !== null

      // Optimistically update the local state immediately
      this.booleanFilters[paramName] = !isCurrentlyActive

      if (isCurrentlyActive) {
        // Remove filter if already active (toggle off)
        url.searchParams.delete(paramName)
      } else {
        // Apply filter with the true value
        url.searchParams.set(paramName, value)
      }

      // Reset to page 1 when filtering
      url.searchParams.delete('page')

      this.applyFilter(url)
    },

    clearAllFilters() {
      const url = new URL(window.location.href)

      // Remove all filter parameters
      const paramsToDelete = []
      for (const [key] of url.searchParams) {
        if (key.startsWith('filter.')) {
          paramsToDelete.push(key)
        }
      }

      paramsToDelete.forEach(param => url.searchParams.delete(param))

      // Reset to page 1
      url.searchParams.delete('page')

      // Close mobile drawer
      this.mobileFiltersOpen = false

      this.applyFilter(url)
    }
  }
}
